import { useState, useEffect, useRef, useMemo, memo } from 'react';
import FeatureCard from '../Components/FeatureCard';
import { NavLink } from 'react-router-dom';
import { FaQuoteLeft, FaArrowRight, FaArrowLeft, FaChevronLeft, FaChevronRight, FaCheck, FaEye, FaUser, FaChalkboardTeacher, FaMobileAlt, FaSignInAlt, FaWhatsapp, FaShoppingCart, FaStore, FaCalendarAlt, FaClipboardList, FaUserTie, FaInfoCircle, FaEnvelope, FaStar, FaUsers, FaHeart, FaThumbsUp, FaGraduationCap, FaUserCheck, FaClock, FaHandsHelping } from 'react-icons/fa';
// Import HttpInterceptor explicitly to avoid SSR/default import issues
import HttpInterceptor from '../lib/HttpInterceptor';
import StarRating from '../Components/StarRating';
import PWAInstallSection from '../Components/PWAInstallSection';
import { QRCodeSVG } from 'qrcode.react';

// Fallback placeholder image
const placeholderImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAABTSURBVFhH7c6xCQAwDAJQ+9+rgkvw7gI2wSU4b0JGRGRERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERGRk3v0C3wI8yB6rRAAAAAElFTkSuQmCC';

// Dynamically import images using import.meta.glob
const imageModules = import.meta.glob('../images/hero-bg-*.jpg', { eager: true });
const heroImagesRaw = [
  '../images/hero-bg-1.jpg',
  '../images/hero-bg-2.jpg',
  '../images/hero-bg-3.jpg',
  '../images/hero-bg-4.jpg',
  '../images/hero-bg-5.jpg',
  '../images/hero-bg-6.jpg',
].map((path) => {
  try {
    const module = imageModules[path];
    if (module && module.default) {
      return module.default;
    }
    // Silently skip missing images (they'll be filtered out)
    return null;
  } catch (error) {
    // Silently handle import errors
    return null;
  }
});

// Filter valid images, use placeholder if none are valid
const heroImages = heroImagesRaw.filter(Boolean).length > 0 ? heroImagesRaw.filter(Boolean) : [placeholderImage];

// Component to handle testimonial avatar with fallback
const TestimonialAvatar = ({ src, name, size = 12 }) => {
  const [imageError, setImageError] = useState(false);
  const sizeClass = size === 10 ? 'w-10 h-10' : 'w-12 h-12';
  const marginClass = size === 10 ? 'mr-3' : 'mr-4';
  const textSizeClass = size === 10 ? 'text-sm' : 'text-sm';

  if (imageError || !src) {
    return (
      <div className={`${sizeClass} rounded-full bg-teal-100 flex items-center justify-center ${marginClass} ring-2 ring-teal-500`}>
        <span className={`text-teal-600 font-semibold ${textSizeClass}`}>
          {name.charAt(0).toUpperCase()}
        </span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className={`${sizeClass} rounded-full object-cover ${marginClass} ring-2 ring-green-500`}
      onError={() => setImageError(true)}
    />
  );
};

function VisitorCounter() {
  const [count, setCount] = useState(0);
  const [targetCount, setTargetCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const trackingInProgress = useRef(false); // Prevent double-tracking
  const animationFrameRef = useRef(null); // Track animation frame
  const lastAnimatedTargetRef = useRef(0); // Track last animated target to prevent re-animation
  const countRef = useRef(0); // Track count value for animation

  // Track visitor only on page refresh or revisit (not on scroll or visibility changes)
  useEffect(() => {
    const sessionKey = 'visitor-tracked-session';
    const hasTrackedThisSession = sessionStorage.getItem(sessionKey);

    // Helper function to track visitor
    const trackVisitor = async () => {
      if (trackingInProgress.current) return;

      // Ensure HttpInterceptor is available
      if (!HttpInterceptor || typeof HttpInterceptor.post !== 'function') {
        return;
      }

      trackingInProgress.current = true;
      try {
        // Call HttpInterceptor.post directly (not through default)
        const response = await HttpInterceptor.post('/api/visitors/track', {}, {
          showErrorToast: false
        });

        // Mark as tracked in this session
        sessionStorage.setItem(sessionKey, 'true');

        // Fetch updated count after tracking
        setTimeout(() => {
          fetchVisitorCount();
          trackingInProgress.current = false;
        }, 300);
      } catch (error) {
        console.error('Error tracking visitor:', error);
        fetchVisitorCount();
        trackingInProgress.current = false;
      }
    };

    // Check navigation type using modern PerformanceNavigationTiming API
    const getNavigationType = () => {
      try {
        const navEntries = performance.getEntriesByType('navigation');
        if (navEntries.length > 0) {
          return navEntries[0].type; // 'navigate', 'reload', 'back_forward', or 'prerender'
        }
      } catch (e) {
        // Fallback to deprecated API
        if (performance.navigation) {
          const type = performance.navigation.type;
          if (type === 1) return 'reload'; // TYPE_RELOAD
          if (type === 2) return 'back_forward'; // TYPE_BACK_FORWARD
          return 'navigate';
        }
      }
      return 'navigate';
    };

    const navType = getNavigationType();
    const isReload = navType === 'reload';
    const isBackForward = navType === 'back_forward';
    const isRevisit = isReload || isBackForward;

    // Track visitor if:
    // 1. First visit in this session (not tracked yet), OR
    // 2. Page was reloaded (refresh), OR
    // 3. User navigated back/forward (revisit)
    if (!hasTrackedThisSession || isRevisit) {
      trackVisitor();
    } else {
      // Already tracked in this session and not a reload/revisit, just fetch count
      fetchVisitorCount();
    }

    // Listen for pageshow event (fires on page load, refresh, and back/forward navigation)
    // This handles cases where navigation type might not be detected correctly
    const handlePageShow = (event) => {
      // If page was loaded from cache (back/forward navigation), track as revisit
      if (event.persisted && !hasTrackedThisSession) {
        trackVisitor();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []); // Empty dependency array - only run on mount

  // Fetch visitor count from server
  const fetchVisitorCount = async () => {
    // Ensure HttpInterceptor is available
    if (!HttpInterceptor || typeof HttpInterceptor.get !== 'function') {
      console.error('HttpInterceptor is not available or get method is not a function');
      return;
    }

    try {
      const response = await HttpInterceptor.get('/api/visitors/count', {
        showErrorToast: false
      }).catch((err) => {
        console.error('Network error fetching visitor count:', err);
        return null;
      });

      if (response?.status === 200) {
        // Handle different response formats
        const totalVisitors = response.data?.data?.total ?? response.data?.total ?? 0;
        setTargetCount(totalVisitors); // Always set, even if 0
      } else {
        // Silent failure if status is not 200
      }
    } catch (error) {
      // Silent failure on catch
    }
  };

  // Initial fetch of visitor count
  useEffect(() => {
    fetchVisitorCount();
  }, []);

  // Update count ref when count changes
  useEffect(() => {
    countRef.current = count;
  }, [count]);

  // Show section when component mounts (no need to wait for intersection)
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Animate count when targetCount changes
  useEffect(() => {
    if (!isVisible || targetCount === 0) return;

    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Don't re-animate to the same target if we've already animated to it
    if (lastAnimatedTargetRef.current === targetCount) return;

    // Get the current count from ref
    const startCount = countRef.current;

    // If already at target, mark as animated and return
    if (targetCount === startCount) {
      lastAnimatedTargetRef.current = targetCount;
      return;
    }

    const duration = 2000;
    const startTime = Date.now();

    const animateCount = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const newCount = Math.floor(startCount + (progress * (targetCount - startCount)));

      setCount(newCount);
      countRef.current = newCount;

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animateCount);
      } else {
        setCount(targetCount);
        countRef.current = targetCount;
        lastAnimatedTargetRef.current = targetCount;
        animationFrameRef.current = null;
      }
    };

    animationFrameRef.current = requestAnimationFrame(animateCount);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isVisible, targetCount]);

  return (
    <section
      ref={sectionRef}
      className="bg-gradient-to-r from-primary to-dark py-4 text-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Mobile/PWA: Two lines (visible on screens smaller than sm breakpoint) */}
        <div className="block sm:hidden">
          <div className="flex flex-col items-center">
            <p className="flex items-center justify-center mb-1">
              <FaEye className="mr-2" />
              <span className="font-medium">{count.toLocaleString()}+</span> happy visitors and growing!
            </p>
            <p className="text-sm">We're glad you're here.</p>
          </div>
        </div>
        {/* Desktop: Single line (visible on sm and larger screens) */}
        <p className="hidden sm:flex items-center justify-center">
          <FaEye className="mr-2" />
          <span className="font-medium">{count.toLocaleString()}+</span> happy visitors and growing! We're glad you're here.
        </p>
      </div>
    </section>
  );
}

function Accordion({ question, answer, index, isOpen, onClick }) {
  return (
    <div className="border-b border-gray-200 animate-slide-in" style={{ animationDelay: `${index * 100}ms` }}>
      <button
        className="w-full text-left py-4 px-6 flex justify-between items-center hover:bg-gray-50 transition-colors"
        onClick={onClick}
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${index}`}
      >
        <h3 className="text-lg font-semibold text-gray-900">{question}</h3>
        <span className="text-teal-600 text-xl">{isOpen ? '−' : '+'}</span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96' : 'max-h-0'
          }`}
      >
        <div id={`faq-answer-${index}`} className="px-6 py-4 bg-white">
          <p className="text-gray-600">{answer}</p>
        </div>
      </div>
    </div>
  );
}

// Stats Section Component - Fetches real data from backend
// Defined outside Landing to prevent recreation on every render
const StatsSection = memo(({ compact = false }) => {
  // Initialize with default values - set once, update once when data loads, then never change
  const [stats, setStats] = useState({
    averageRating: 0,
    satisfactionRate: 100,
    activeMentors: 0,
    studentReviews: 0,
    hasReviews: false,
    isAllDefault: false
  });
  const hasFetchedRef = useRef(false); // Prevent multiple fetches
  const hasUpdatedRef = useRef(false); // Prevent multiple state updates

  useEffect(() => {
    // Only fetch once
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchStats = async () => {
      try {
        // Fetch from public stats endpoint
        const response = await HttpInterceptor.get('/api/admin/public/stats', {
          showErrorToast: false
        }).catch(() => null);

        if (response?.status === 200 && response.data?.data) {
          const data = response.data.data;
          const activeTrainers = data.trainers?.active || 0;

          // Get average rating, total reviews, and satisfaction rate from admin stats
          let averageRating = data.reviews?.averageRating || 0;

          const aiCount = 6;
          const aiRating = 4.0;
          
          // Combine the AI rating with mentor rating based on proportion of mentors
          if (activeTrainers > 0 || isAllDefault) {
            // averageRating is the current mentor average. 
            // Average it out with the 6 AI consultants
            const totalTrainers = activeTrainers + aiCount;
            const weightedMentorRating = averageRating * activeTrainers;
            const weightedAIRating = aiRating * aiCount;
            averageRating = Number(((weightedMentorRating + weightedAIRating) / totalTrainers).toFixed(1));
          } else {
            averageRating = aiRating;
          }

          const totalReviews = data.reviews?.totalReviews || 0;
          const satisfactionRate = data.reviews?.satisfactionRate || 0;
          const isAllDefault = data.reviews?.isAllDefault || false;

          // Show 100% satisfaction when no reviews exist (better UX than showing 0%)
          const displaySatisfactionRate = totalReviews === 0 ? 100 : satisfactionRate;

          // Update state only once with final values
          if (!hasUpdatedRef.current) {
            hasUpdatedRef.current = true; // Set flag immediately to prevent race conditions
            setStats({
              averageRating: averageRating,
              satisfactionRate: displaySatisfactionRate,
              activeMentors: activeTrainers || 0,
              studentReviews: totalReviews || 0,
              hasReviews: totalReviews > 0,
              isAllDefault: isAllDefault
            });
          }
        }
      } catch (error) {
        // Keep default values if API fails (already set in initial state)
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
  }, []);

  const formatNumber = (num) => {
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K+`;
    if (num >= 100) return `${num}+`;
    return num.toString();
  };

  const renderStars = (rating) => {
    return <StarRating rating={rating || 0} size="sm" />;
  };

  if (compact) {
    return (
      <section className="py-6 sm:py-8 px-4 sm:px-6 bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-teal-400/60 to-transparent"></div>
        <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-teal-100/30 to-blue-100/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-indigo-100/20 to-purple-100/10 rounded-full blur-3xl"></div>

        <div className="max-w-3xl mx-auto relative z-10">
          <div className="text-center mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-teal-900 mb-1">Platform Statistics</h3>
            <p className="text-xs sm:text-sm text-teal-600/70">Trusted by thousands of members</p>
          </div>

          <div className="grid grid-cols-2 gap-2.5 sm:gap-3 max-w-2xl mx-auto">
            {/* Rating Card */}
            <div className="bg-gradient-to-br from-white to-teal-50/50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center border-2 border-teal-200/50 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-12 h-12 bg-gradient-to-br from-teal-200/20 to-blue-200/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="flex justify-center mb-1.5">
                  <div className="bg-gradient-to-br from-yellow-100 to-amber-100 p-1.5 sm:p-2 rounded-full">
                    <FaStar className="text-yellow-500 text-sm sm:text-base" />
                  </div>
                </div>
                <div className="text-xl sm:text-3xl font-bold text-teal-900 mb-0.5">{stats.averageRating}</div>
                <div className="flex justify-center mb-1 scale-75 sm:scale-100">{renderStars(stats.averageRating)}</div>
                <p className="text-[10px] sm:text-xs font-semibold text-teal-700">Rating</p>
              </div>
            </div>

            {/* Mentors Card */}
            <div className="bg-gradient-to-br from-white to-blue-50/50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center border-2 border-blue-200/50 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-12 h-12 bg-gradient-to-br from-blue-200/20 to-indigo-200/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="flex justify-center mb-1.5">
                  <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-1.5 sm:p-2 rounded-full">
                    <FaUsers className="text-blue-500 text-sm sm:text-base" />
                  </div>
                </div>
                <div className="text-xl sm:text-3xl font-bold text-blue-900 mb-0.5">{formatNumber(stats.activeMentors)}</div>
                <p className="text-[10px] sm:text-xs font-semibold text-blue-700">Mentors</p>
              </div>
            </div>

            {/* Satisfaction Card */}
            <div className="bg-gradient-to-br from-white to-indigo-50/50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center border-2 border-indigo-200/50 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-12 h-12 bg-gradient-to-br from-indigo-200/20 to-purple-200/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="flex justify-center mb-1.5">
                  <div className="bg-gradient-to-br from-green-100 to-emerald-100 p-1.5 sm:p-2 rounded-full">
                    <FaHeart className="text-green-500 text-sm sm:text-base" />
                  </div>
                </div>
                <div className="text-xl sm:text-3xl font-bold text-indigo-900 mb-0.5">{stats.satisfactionRate}%</div>
                <p className="text-[10px] sm:text-xs font-semibold text-indigo-700">Satisfaction</p>
              </div>
            </div>

            {/* Reviews Card */}
            <div className="bg-gradient-to-br from-white to-cyan-50/50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center border-2 border-cyan-200/50 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-12 h-12 bg-gradient-to-br from-cyan-200/20 to-teal-200/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="flex justify-center mb-1.5">
                  <div className="bg-gradient-to-br from-cyan-100 to-teal-100 p-1.5 sm:p-2 rounded-full">
                    <FaThumbsUp className="text-cyan-500 text-sm sm:text-base" />
                  </div>
                </div>
                <div className="text-xl sm:text-3xl font-bold text-cyan-900 mb-0.5">{formatNumber(stats.studentReviews || 0)}</div>
                <p className="text-[10px] sm:text-xs font-semibold text-cyan-700">Reviews</p>
                {!stats.hasReviews && stats.studentReviews === 0 && (
                  <p className="text-[9px] text-cyan-600/60 mt-0.5 italic">Coming soon</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-r from-green-600 to-green-800 text-white" style={{ animation: 'none', transition: 'none' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
          <div className="p-6" style={{ animation: 'none', transition: 'none' }}>
            <div className="text-5xl font-bold mb-2" style={{ animation: 'none', transition: 'none' }}>{stats.averageRating}</div>
            <div className="flex justify-center mb-2" style={{ animation: 'none', transition: 'none' }}>
              {renderStars(stats.averageRating)}
            </div>
            <p className="text-white/80">Average Mentor Rating</p>
            {stats.isAllDefault && (
              <p className="text-white/60 text-xs mt-1 italic">Default rating</p>
            )}
          </div>
          <div className="p-6" style={{ animation: 'none', transition: 'none' }}>
            <div className="text-5xl font-bold mb-2" style={{ animation: 'none', transition: 'none' }}>{stats.satisfactionRate}%</div>
            <p className="text-white/80">Student Satisfaction</p>
            {!stats.hasReviews && (
              <p className="text-white/60 text-xs mt-1 italic">No reviews submitted yet</p>
            )}
          </div>
          <div className="p-6" style={{ animation: 'none', transition: 'none' }}>
            <div className="text-5xl font-bold mb-2" style={{ animation: 'none', transition: 'none' }}>{formatNumber(stats.activeMentors)}</div>
            <p className="text-white/80">Active Mentors</p>
          </div>
          <div className="p-6" style={{ animation: 'none', transition: 'none' }}>
            <div className="text-5xl font-bold mb-2" style={{ animation: 'none', transition: 'none' }}>{formatNumber(stats.studentReviews || 4)}</div>
            <p className="text-white/80">Member Reviews</p>
            {!stats.hasReviews && (
              <p className="text-white/60 text-xs mt-1 italic">Default reviews</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
});

function Landing() {
  // Detect PWA standalone mode
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    // Listen for changes (e.g., if user installs PWA)
    window.addEventListener('resize', checkPWA);
    return () => window.removeEventListener('resize', checkPWA);
  }, []);

  const features = [
    {
      title: 'Expert Mentors',
      description: 'Learn from certified mentors with years of experience and specialized training.',
      icon: <FaGraduationCap />,
      color: 'teal',
    },
    {
      title: 'Personalized Approach',
      description: 'Get customized guidance tailored to your skill level and personal goals.',
      icon: <FaUserCheck />,
      color: 'blue',
    },
    {
      title: 'Flexible Scheduling',
      description: 'Book sessions at your convenience with our flexible scheduling system.',
      icon: <FaClock />,
      color: 'indigo',
    },
    {
      title: 'Community Hub',
      description: 'Connect with mentors, share tips, and join group sessions.',
      icon: <FaHandsHelping />,
      color: 'cyan',
    },
  ];

  const faqs = [
    {
      question: 'How do I choose the right mentor?',
      answer: 'Our matching system helps you find mentors based on your goals, experience level, and preferred teaching style.',
    },
    {
      question: 'What if I need to cancel a session?',
      answer: 'You can cancel sessions up to 24 hours in advance with no penalty through your dashboard.',
    },
    {
      question: 'Do you offer group classes?',
      answer: 'Yes, many of our mentors offer both private and group sessions to suit different preferences.',
    },
    {
      question: 'Can I try Yogvital for free?',
      answer: 'Yes, enjoy a free trial to explore all features.',
    },
    {
      question: 'How secure is my data?',
      answer: 'We use top-tier encryption to keep your data safe.',
    },
  ];

  // Testimonials will be fetched from real student reviews
  const [testimonials, setTestimonials] = useState([]);

  const [openIndex, setOpenIndex] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  // Sample testimonials as fallback (for demo/testing)
  const sampleTestimonials = [
    {
      name: 'Priya Sharma',
      role: 'Yogvital Member',
      quote: 'Yogvital has transformed my yoga practice! The mentors are amazing and the personalized approach really helped me progress. Highly recommended!',
      rating: 5,
      avatar: null
    },
    {
      name: 'Rajesh Kumar',
      role: 'Yogvital Member',
      quote: 'I\'ve been practicing yoga for years, but Yogvital took it to the next level. The flexibility in scheduling and quality of instruction is outstanding.',
      rating: 5,
      avatar: null
    },
    {
      name: 'Anjali Patel',
      role: 'Yogvital Member',
      quote: 'The best decision I made for my wellness journey. My trainer is patient, knowledgeable, and truly cares about my progress. Thank you Yogvital!',
      rating: 5,
      avatar: null
    },
    {
      name: 'Suresh Menon',
      role: 'Yogvital Member',
      quote: 'Excellent platform! Easy to use, great mentors, and the community support is wonderful. I\'ve seen significant improvements in my flexibility and strength.',
      rating: 5,
      avatar: null
    }
  ];

  // Fetch testimonials from real student reviews
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        // Try to fetch visible testimonials from public endpoint
        const response = await HttpInterceptor.get('/api/admin/public/testimonials', {
          showErrorToast: false
        }).catch(() => null);

        if (response?.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          // Format testimonials from API response
          const formattedTestimonials = response.data.data.map((review) => ({
            name: review.name || 'Member',
            role: review.role || 'Yogvital Member',
            quote: review.quote || '',
            rating: review.rating || 5,
            avatar: review.avatar || null
          })).filter((t) => t.quote && t.quote.trim().length > 0);

          setTestimonials(formattedTestimonials);
        } else {
          // Use sample testimonials as fallback if no data from API
          setTestimonials(sampleTestimonials);
        }
      } catch (error) {
        // On error, use sample testimonials as fallback
        setTestimonials(sampleTestimonials);
      }
    };

    fetchTestimonials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const validImageCount = heroImages.length;
  const sliderRef = useRef(null);
  const intervalRef = useRef(null);

  const renderStars = (rating) => {
    return <StarRating rating={rating || 0} size="sm" />;
  };

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const goToSlide = (index) => {
    if (index < 0 || index >= validImageCount) return;
    setCurrentSlide(index);
    if (validImageCount > 1) {
      resetAutoPlay();
    }
  };

  const nextSlide = () => {
    if (isTransitioning || validImageCount <= 1 || !sliderRef.current) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => {
      if (prev === validImageCount - 1) {
        setTimeout(() => {
          if (sliderRef.current) {
            sliderRef.current.style.transition = 'none';
          }
          setCurrentSlide(0);
          setTimeout(() => {
            if (sliderRef.current) {
              sliderRef.current.style.transition = 'transform 1s ease-in-out';
            }
            setIsTransitioning(false);
          }, 50);
        }, 1000);
        return prev;
      }
      setIsTransitioning(false);
      return (prev + 1) % validImageCount;
    });
  };

  const prevSlide = () => {
    if (isTransitioning || validImageCount <= 1 || !sliderRef.current) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => {
      if (prev === 0) {
        setTimeout(() => {
          if (sliderRef.current) {
            sliderRef.current.style.transition = 'none';
          }
          setCurrentSlide(validImageCount - 1);
          setTimeout(() => {
            if (sliderRef.current) {
              sliderRef.current.style.transition = 'transform 1s ease-in-out';
            }
            setIsTransitioning(false);
          }, 50);
        }, 1000);
        return prev;
      }
      setIsTransitioning(false);
      return (prev - 1 + validImageCount) % validImageCount;
    });
  };

  const resetAutoPlay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (validImageCount > 1) {
      intervalRef.current = setInterval(nextSlide, 5000);
    }
  };

  // Navigation functions for testimonials
  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    if (validImageCount > 1) {
      intervalRef.current = setInterval(nextSlide, 5000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validImageCount]);

  // PWA Mode - Simplified App-like Landing Page
  if (isPWA) {
    return (
      <div className="bg-gradient-to-b from-teal-50/40 via-blue-50/30 to-white min-h-screen">
        {/* Compact Hero Section */}
        <section className="relative py-12 px-4 overflow-hidden">
          {/* Hero background carousel (PWA) - No overlay for better UX */}
          <div
            ref={sliderRef}
            className="absolute inset-0 z-0 flex transition-transform duration-1000 ease-in-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {heroImages.map((image, index) => (
              <div key={index} className="w-full h-full flex-shrink-0 relative">
                <img
                  src={image}
                  alt={`Yoga background ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  onError={(e) => {
                    e.target.src = placeholderImage;
                  }}
                />
                {/* Minimal gradient overlay only at top for text readability */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-transparent"></div>
              </div>
            ))}
          </div>

          {/* Optional slide indicators (PWA) */}
          {validImageCount > 1 && (
            <div className="absolute bottom-4 left-0 right-0 z-[2] flex justify-center space-x-2">
              {heroImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${currentSlide === index ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/70'
                    }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}

          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] [text-shadow:_2px_2px_4px_rgba(0,0,0,0.8)]">Transform Your Practice</h1>
            <p className="text-lg md:text-xl text-white mb-6 max-w-2xl mx-auto drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)] [text-shadow:_1px_1px_3px_rgba(0,0,0,0.7)] font-medium">
              Connect with certified mentors
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <NavLink
                to="/services"
                className="bg-gradient-to-r from-teal-500 to-blue-500 text-white hover:from-teal-600 hover:to-blue-600 px-8 py-3 rounded-xl font-semibold text-base transition-all shadow-lg ring-2 ring-teal-300/50 active:scale-95 inline-flex items-center justify-center relative z-20 cursor-pointer"
              >
                <FaChalkboardTeacher className="mr-2" />
                <span>Find Your Mentor</span>
                <FaArrowRight className="ml-2" />
              </NavLink>
              <NavLink
                to="/signup"
                className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-8 py-3 rounded-xl font-semibold text-base transition-all shadow-lg ring-2 ring-indigo-300/50 active:scale-95 inline-flex items-center justify-center"
              >
                <FaUser className="mr-2" />
                <span>Get Started</span>
              </NavLink>
            </div>
          </div>
        </section>

        <VisitorCounter />

        {/* Quick Actions Section - PWA Mode */}
        <section className="pt-10 sm:pt-12 pb-6 sm:py-8 bg-gradient-to-b from-white via-teal-50/30 to-white relative">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-teal-400/60 to-transparent"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <div className="relative">
                <div className="absolute left-0 w-1.5 h-7 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                <div className="relative w-1.5 h-7 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-teal-900 drop-shadow-sm">Quick Actions</h2>
                <p className="text-xs text-teal-600/70 mt-0.5 font-medium">Access key features</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-white to-teal-50/40 via-blue-50/20 rounded-2xl shadow-2xl border-2 border-teal-200/50 hover:shadow-3xl transition-all duration-300 p-4 sm:p-5 relative overflow-hidden">
              {/* Decorative overlays */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-teal-100/20 to-blue-100/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-br from-blue-100/20 to-indigo-100/10 rounded-full blur-3xl"></div>

              <div className="flex flex-nowrap gap-3 sm:gap-4 overflow-x-auto overflow-y-visible pb-2 scrollbar-hide -mx-2 px-2 sm:mx-0 sm:px-0 pt-2 relative z-10">
                {[
                  {
                    title: 'Services',
                    icon: <FaHandsHelping className="text-blue-600" />,
                    link: '/services',
                    bgColor: 'bg-blue-100',
                    textColor: 'text-blue-700'
                  },
                  {
                    title: 'Marketplace',
                    icon: <FaStore className="text-teal-600" />,
                    link: '/Market',
                    bgColor: 'bg-teal-100',
                    textColor: 'text-teal-700'
                  },
                  {
                    title: 'Sign Up',
                    icon: <FaUser className="text-indigo-600" />,
                    link: '/signup',
                    bgColor: 'bg-indigo-100',
                    textColor: 'text-indigo-700'
                  },
                  {
                    title: 'Login',
                    icon: <FaSignInAlt className="text-teal-600" />,
                    link: '/login',
                    bgColor: 'bg-teal-100',
                    textColor: 'text-teal-700'
                  },
                  {
                    title: 'About',
                    icon: <FaInfoCircle className="text-blue-600" />,
                    link: '/about',
                    bgColor: 'bg-blue-100',
                    textColor: 'text-blue-700'
                  },
                  {
                    title: 'Contact Us',
                    icon: <FaEnvelope className="text-indigo-600" />,
                    link: '/contact-form',
                    bgColor: 'bg-indigo-100',
                    textColor: 'text-indigo-700'
                  }
                ].map((item, index) => (
                  <NavLink
                    key={index}
                    to={item.link}
                    state={item.state}
                    className="bg-gradient-to-br from-white to-teal-50/30 text-gray-700 p-4 sm:p-5 rounded-2xl shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-2 border-teal-100/50 hover:border-teal-300/50 active:scale-95 flex flex-col items-center text-center group relative overflow-visible flex-shrink-0 min-w-[110px] sm:min-w-[130px]"
                    onClick={() => {
                      if (item.state?.openServices) {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                  >
                    {/* Decorative gradient overlay - behind content */}
                    <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-teal-200/20 to-blue-200/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0"></div>

                    {/* Top accent line on hover */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-teal-300/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    <div className={`${item.bgColor} p-3 sm:p-3.5 rounded-xl mb-2 group-hover:scale-110 transition-transform duration-300 shadow-md ring-2 ring-teal-100/50 relative z-10`}>
                      <div className="text-2xl sm:text-3xl">
                        {item.icon}
                      </div>
                    </div>
                    <h3 className={`text-[10px] sm:text-xs font-semibold ${item.textColor} relative z-10 leading-tight`}>{item.title}</h3>
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Compact Features - Enhanced Visual Grid */}
        <section className="py-8 sm:py-10 px-4 bg-gradient-to-b from-white via-teal-50/30 to-white relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-teal-400/60 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-200/40 to-transparent"></div>

          <div className="max-w-4xl mx-auto relative z-10">
            <div className="text-center mb-6 sm:mb-8">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="relative">
                  <div className="absolute left-0 w-1.5 h-6 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                  <div className="relative w-1.5 h-6 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-teal-900">Why Choose Us</h2>
                <div className="relative">
                  <div className="absolute left-0 w-1.5 h-6 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                  <div className="relative w-1.5 h-6 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-teal-600/70">Discover what makes us different</p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {features.map((feature, index) => {
                const colorMap = {
                  teal: {
                    bg: 'from-teal-50 to-blue-50',
                    border: 'border-teal-200/60',
                    iconBg: 'bg-gradient-to-br from-teal-100 to-blue-100',
                    iconColor: 'text-teal-600',
                    textColor: 'text-teal-800',
                    hoverBorder: 'hover:border-teal-300/80'
                  },
                  blue: {
                    bg: 'from-blue-50 to-indigo-50',
                    border: 'border-blue-200/60',
                    iconBg: 'bg-gradient-to-br from-blue-100 to-indigo-100',
                    iconColor: 'text-blue-600',
                    textColor: 'text-blue-800',
                    hoverBorder: 'hover:border-blue-300/80'
                  },
                  indigo: {
                    bg: 'from-indigo-50 to-purple-50',
                    border: 'border-indigo-200/60',
                    iconBg: 'bg-gradient-to-br from-indigo-100 to-purple-100',
                    iconColor: 'text-indigo-600',
                    textColor: 'text-indigo-800',
                    hoverBorder: 'hover:border-indigo-300/80'
                  },
                  cyan: {
                    bg: 'from-cyan-50 to-teal-50',
                    border: 'border-cyan-200/60',
                    iconBg: 'bg-gradient-to-br from-cyan-100 to-teal-100',
                    iconColor: 'text-cyan-600',
                    textColor: 'text-cyan-800',
                    hoverBorder: 'hover:border-cyan-300/80'
                  }
                };
                const colors = colorMap[feature.color] || colorMap.teal;

                return (
                  <div
                    key={index}
                    className={`bg-gradient-to-br ${colors.bg} p-4 sm:p-5 rounded-xl sm:rounded-2xl border-2 ${colors.border} ${colors.hoverBorder} text-center shadow-md hover:shadow-xl transition-all duration-300 relative overflow-hidden group`}
                  >
                    {/* Decorative gradient overlay on hover */}
                    <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${colors.iconBg.replace('from-', 'from-').replace('to-', 'to-')} rounded-full blur-2xl opacity-0 group-hover:opacity-30 transition-opacity duration-300`}></div>

                    <div className="relative z-10">
                      {/* Icon */}
                      <div className={`${colors.iconBg} w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 rounded-full flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
                        <div className={`${colors.iconColor} text-xl sm:text-2xl`}>
                          {feature.icon}
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className={`${colors.textColor} font-bold text-sm sm:text-base mb-1.5 group-hover:scale-105 transition-transform duration-300`}>
                        {feature.title}
                      </h3>

                      {/* Description */}
                      <p className="text-[10px] sm:text-xs text-gray-600 leading-tight line-clamp-2">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Compact Stats - Different Color Scheme */}
        <StatsSection compact={true} />

        {/* Testimonials Section - Enhanced PWA Mode */}
        {testimonials.length > 0 && (
          <section className="py-10 sm:py-14 px-4 bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 relative overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-teal-400/60 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-200/40 to-transparent"></div>

            <div className="max-w-4xl mx-auto relative z-10">
              {/* Section Header */}
              <div className="text-center mb-6 sm:mb-8">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div className="relative">
                    <div className="absolute left-0 w-1.5 h-6 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                    <div className="relative w-1.5 h-6 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-teal-900">What Our Members Say</h2>
                  <div className="relative">
                    <div className="absolute left-0 w-1.5 h-6 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                    <div className="relative w-1.5 h-6 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-teal-600/70 max-w-2xl mx-auto">
                  Hear from members who have transformed their practice with our mentors
                </p>
              </div>

              {/* Testimonial Cards */}
              <div className="mb-6 sm:mb-8">
                {testimonials.map((testimonial, index) => (
                  <div
                    key={index}
                    className={`transition-all duration-500 ${index === activeTestimonial
                      ? 'opacity-100 block scale-100'
                      : 'opacity-0 pointer-events-none hidden scale-95'
                      }`}
                  >
                    <div className="bg-gradient-to-br from-white to-teal-50/30 p-5 sm:p-7 rounded-2xl shadow-xl max-w-2xl mx-auto border-2 border-teal-200/60 hover:border-teal-300/80 transition-all duration-300 relative overflow-hidden group">
                      {/* Decorative gradient overlay */}
                      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-100/20 to-blue-100/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                      <div className="relative z-10">
                        {/* Rating Stars */}
                        <div className="mb-3 sm:mb-4 flex items-center gap-1">
                          {renderStars(testimonial.rating)}
                        </div>

                        {/* Quote Icon */}
                        <div className="mb-3 sm:mb-4">
                          <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-teal-100 to-blue-100 shadow-md">
                            <FaQuoteLeft className="text-teal-500 text-lg sm:text-xl" />
                          </div>
                        </div>

                        {/* Quote Text */}
                        <p className="text-gray-700 mb-4 sm:mb-5 italic text-sm sm:text-base leading-relaxed break-words relative pl-2 border-l-2 border-teal-200/50">
                          {testimonial.quote}
                        </p>

                        {/* Author Info */}
                        <div className="flex items-center pt-3 sm:pt-4 border-t border-teal-100/50">
                          {testimonial.avatar ? (
                            <TestimonialAvatar
                              src={testimonial.avatar}
                              name={testimonial.name}
                              size={10}
                            />
                          ) : (
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-teal-100 to-blue-100 flex items-center justify-center mr-3 sm:mr-4 ring-2 ring-teal-300/50 shadow-md flex-shrink-0">
                              <span className="text-teal-600 font-bold text-sm sm:text-base">
                                {testimonial.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <h4 className="font-bold text-gray-800 text-sm sm:text-base break-words">{testimonial.name}</h4>
                            <p className="text-gray-500 text-xs sm:text-sm break-words flex items-center gap-1 mt-0.5">
                              <span className="w-1 h-1 rounded-full bg-teal-400"></span>
                              {testimonial.role}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Enhanced Navigation Controls */}
              {testimonials.length > 1 && (
                <div className="flex items-center justify-center gap-3 sm:gap-4">
                  {/* Previous Button */}
                  <button
                    onClick={prevTestimonial}
                    className="bg-gradient-to-br from-white to-teal-50 rounded-full p-2.5 sm:p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 border-2 border-teal-200/60 hover:border-teal-400 flex-shrink-0 group"
                    aria-label="Previous testimonial"
                  >
                    <FaChevronLeft className="text-teal-500 text-base sm:text-lg group-hover:text-teal-600 transition-colors" />
                  </button>

                  {/* Testimonial Indicator Dots */}
                  <div className="flex justify-center items-center gap-2">
                    {testimonials.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveTestimonial(index)}
                        className={`h-2 rounded-full transition-all duration-300 ${index === activeTestimonial
                          ? 'bg-gradient-to-r from-teal-500 to-blue-500 w-6 sm:w-8 shadow-md'
                          : 'bg-gray-300 w-2 hover:bg-gray-400'
                          }`}
                        aria-label={`View testimonial ${index + 1}`}
                      />
                    ))}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={nextTestimonial}
                    className="bg-gradient-to-br from-white to-teal-50 rounded-full p-2.5 sm:p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 border-2 border-teal-200/60 hover:border-teal-400 flex-shrink-0 group"
                    aria-label="Next testimonial"
                  >
                    <FaChevronRight className="text-teal-500 text-base sm:text-lg group-hover:text-teal-600 transition-colors" />
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Find Your Mentor Section */}
        <section className="py-12 px-4 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-br from-teal-50 to-blue-100 rounded-2xl p-8 border-2 border-teal-200">
              <h2 className="text-2xl font-bold text-teal-900 mb-3">Find Your Perfect Mentor</h2>
              <p className="text-teal-700 mb-6 text-sm max-w-2xl mx-auto">
                Browse certified mentors, read reviews, and find the perfect match for your practice
              </p>
              <NavLink
                to="/services"
                className="inline-flex items-center bg-gradient-to-r from-teal-500 to-blue-500 text-white hover:from-teal-600 hover:to-blue-600 px-8 py-3 rounded-xl font-semibold transition-all shadow-lg ring-2 ring-teal-300/50 active:scale-95 hover:scale-105"
              >
                <FaChalkboardTeacher className="mr-2" />
                <span>Browse Mentors</span>
                <FaArrowRight className="ml-2" />
              </NavLink>
            </div>
          </div>
        </section>

        {/* QR Code Section - Help others install */}
        <section className="py-12 px-4 bg-white">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Share Yogvital</h2>
              <p className="text-gray-600 mb-6 text-sm">
                Help others install the app by sharing this QR code
              </p>
              <div className="flex justify-center mb-4">
                <div className="bg-white p-4 rounded-xl shadow-lg border-2 border-gray-200">
                  <QRCodeSVG
                    value={`${window.location.origin}?install=true`}
                    size={180}
                    level="H"
                    includeMargin={true}
                    fgColor="#00C4B4"
                    bgColor="#ffffff"
                  />
                </div>
              </div>
              <div className="flex justify-center mb-3">
                <button
                  type="button"
                  className="bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-md ring-2 ring-teal-300/50 active:scale-95 inline-flex items-center"
                  onClick={() => {
                    const installUrl = `${window.location.origin}?install=true`;
                    const text = `Install Yogvital: ${installUrl}`;
                    const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;

                    // Prefer native share (PWA-friendly). If user picks WhatsApp, great.
                    // Otherwise, fall back to opening WhatsApp share link directly.
                    if (navigator.share) {
                      navigator
                        .share({ title: 'Yogvital', text: 'Install Yogvital', url: installUrl })
                        .catch(() => {
                          window.open(waUrl, '_blank', 'noopener,noreferrer');
                        });
                      return;
                    }

                    window.open(waUrl, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <FaWhatsapp className="mr-2" />
                  <span>Share on WhatsApp</span>
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Scan with your phone camera to install
              </p>
            </div>
          </div>
        </section>

        {/* Enhanced CTA Section - Get Started Today */}
        <section className="py-12 sm:py-16 px-4 bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-teal-400/60 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-200/40 to-transparent"></div>

          <div className="max-w-3xl mx-auto relative z-10">
            <div className="bg-gradient-to-br from-white to-teal-50/40 rounded-2xl sm:rounded-3xl p-6 sm:p-10 shadow-2xl border-2 border-teal-200/60 relative overflow-hidden">
              {/* Decorative gradient overlays */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-teal-100/30 to-blue-100/20 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-br from-blue-100/30 to-indigo-100/20 rounded-full blur-3xl"></div>

              <div className="relative z-10">
                {/* Section Header */}
                <div className="text-center mb-6 sm:mb-8">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <div className="relative">
                      <div className="absolute left-0 w-1.5 h-6 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                      <div className="relative w-1.5 h-6 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-teal-900">Get Started Today</h2>
                    <div className="relative">
                      <div className="absolute left-0 w-1.5 h-6 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                      <div className="relative w-1.5 h-6 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
                    </div>
                  </div>
                  <p className="text-sm sm:text-base text-teal-600/70 max-w-xl mx-auto">
                    Join our community and start your journey
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
                  <NavLink
                    to="/signup"
                    className="group bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white px-8 py-3.5 sm:py-4 rounded-xl font-bold text-sm sm:text-base transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 inline-flex items-center justify-center w-full sm:w-auto min-w-[180px]"
                  >
                    <FaUser className="mr-2 text-base sm:text-lg group-hover:scale-110 transition-transform" />
                    <span>Sign Up</span>
                  </NavLink>

                  <NavLink
                    to="/login"
                    className="group bg-gradient-to-br from-white to-teal-50/50 border-2 border-teal-300/60 hover:border-teal-400 text-teal-700 hover:text-teal-800 px-8 py-3.5 sm:py-4 rounded-xl font-bold text-sm sm:text-base transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 active:scale-95 inline-flex items-center justify-center w-full sm:w-auto min-w-[180px]"
                  >
                    <FaSignInAlt className="mr-2 text-base sm:text-lg group-hover:scale-110 transition-transform" />
                    <span>Sign In</span>
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Browser Mode - Full Landing Page
  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="relative text-white py-24 overflow-hidden h-screen max-h-[800px]">
        <div
          ref={sliderRef}
          className="absolute inset-0 z-0 flex transition-transform duration-1000 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {heroImages.map((image, index) => (
            <div key={index} className="w-full h-full flex-shrink-0 relative">
              <img
                src={image}
                alt={`Yoga background ${index + 1}`}
                className="w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
                onError={(e) => {
                  e.target.src = placeholderImage;
                }}
              />
            </div>
          ))}
        </div>

        {validImageCount > 1 && (
          <div className="absolute inset-0">
            <button
              onClick={prevSlide}
              className="absolute left-2 xs:left-3 sm:left-4 md:left-6 top-1/2 z-40 -translate-y-1/2 bg-white/30 hover:bg-white/70 border border-white/50 hover:border-white hover:ring-2 hover:ring-white hover:outline hover:outline-2 hover:outline-blue-500 rounded-full p-2 xs:p-3 sm:p-4 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-glow-hover cursor-pointer box-content pointer-events-auto"
              aria-label="Previous slide"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 xs:h-6 xs:w-6 sm:h-7 sm:w-7 text-white pointer-events-auto"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-2 xs:right-3 sm:right-4 md:right-6 top-1/2 z-40 -translate-y-1/2 bg-white/30 hover:bg-white/70 border border-white/50 hover:border-white hover:ring-2 hover:ring-white hover:outline hover:outline-2 hover:outline-blue-500 rounded-full p-2 xs:p-3 sm:p-4 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-glow-hover cursor-pointer box-content pointer-events-auto"
              aria-label="Next slide"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 xs:h-6 xs:w-6 sm:h-7 sm:w-7 text-white pointer-events-auto"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
            <div className="absolute bottom-8 left-0 right-0 z-40 flex justify-center space-x-2">
              {heroImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${currentSlide === index ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/70'
                    }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 h-full flex flex-col justify-center">
          <div className="flex flex-col items-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 drop-shadow-lg">Transform Your Practice</h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto drop-shadow-md">
              Connect with certified mentors and take your practice to the next level
            </p>
            <div className="flex justify-center">
              <NavLink
                to="/signup"
                className="bg-pink-800 hover:bg-pink-900 text-white px-8 py-3 rounded-lg font-medium text-lg transition-colors shadow-lg hover:shadow-xl inline-flex items-center"
              >
                <FaUser className="mr-2" />
                <span>Get Started - For Members & Mentors</span>
                <FaChalkboardTeacher className="ml-2" />
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      <VisitorCounter />

      {/* Features Section */}
      <section id="features" className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">Why Choose Us</h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            We connect passionate members with experienced mentors for transformative experiences
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3 text-gray-800">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section id="testimonials" className="py-16 bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">What Our Members Say</h2>
            <p className="text-xl text-gray-600 text-center mb-16 max-w-3xl mx-auto">
              Hear from members who have transformed their practice with our mentors
            </p>

            <div className="mb-8">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className={`transition-opacity duration-500 ${index === activeTestimonial
                    ? 'opacity-100 block'
                    : 'opacity-0 pointer-events-none hidden'
                    }`}
                >
                  <div className="bg-white p-4 sm:p-8 rounded-xl shadow-lg max-w-2xl mx-auto border-2 border-teal-100 hover:border-teal-200 transition-all">
                    <div className="mb-3 sm:mb-4">
                      {renderStars(testimonial.rating)}
                    </div>
                    <FaQuoteLeft className="text-gray-300 text-xl sm:text-2xl mb-3 sm:mb-4" />
                    <p className="text-gray-700 mb-4 sm:mb-6 italic text-sm sm:text-base leading-relaxed break-words">{testimonial.quote}</p>
                    <div className="flex items-center">
                      {testimonial.avatar ? (
                        <TestimonialAvatar
                          src={testimonial.avatar}
                          name={testimonial.name}
                          size={12}
                        />
                      ) : (
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-teal-100 flex items-center justify-center mr-3 sm:mr-4 ring-2 ring-teal-500 flex-shrink-0">
                          <span className="text-teal-600 font-semibold text-xs sm:text-sm">
                            {testimonial.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-gray-800 text-sm sm:text-base break-words">{testimonial.name}</h4>
                        <p className="text-gray-500 text-xs sm:text-sm break-words">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Controls - Arrows and Dots Aligned */}
            {testimonials.length > 1 && (
              <div className="flex items-center justify-center gap-4 mt-6 sm:mt-8">
                {/* Previous Button */}
                <button
                  onClick={prevTestimonial}
                  className="bg-white rounded-full p-3 shadow-lg hover:bg-teal-50 transition-colors border-2 border-teal-200 hover:border-teal-400 flex-shrink-0"
                  aria-label="Previous testimonial"
                >
                  <FaChevronLeft className="text-teal-200 text-xl" />
                </button>

                {/* Testimonial Indicator Dots */}
                <div className="flex justify-center space-x-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveTestimonial(index)}
                      className={`w-3 h-3 rounded-full transition-all ${index === activeTestimonial ? 'bg-teal-600' : 'bg-gray-300'}`}
                      aria-label={`View testimonial ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Next Button */}
                <button
                  onClick={nextTestimonial}
                  className="bg-white rounded-full p-3 shadow-lg hover:bg-teal-50 transition-colors border-2 border-teal-200 hover:border-teal-400 flex-shrink-0"
                  aria-label="Next testimonial"
                >
                  <FaChevronRight className="text-teal-200 text-xl" />
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Stats Section */}
      <StatsSection />

      {/* For Students Section */}
      <section id="for-students" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">For members</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find the perfect mentor to match your goals and learning style
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-800">Discover Your Ideal Mentor</h3>
              <p className="text-gray-600 mb-6">
                Browse profiles of certified mentors, read reviews from other Members, and find the perfect match for your practice.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <div className="bg-green-100 p-1 rounded-full mr-3 mt-1">
                    <FaCheck className="text-green-600" />
                  </div>
                  <span className="text-gray-700">Filter by style, experience level, and availability</span>
                </li>
                <li className="flex items-start">
                  <div className="bg-green-100 p-1 rounded-full mr-3 mt-1">
                    <FaCheck className="text-green-600" />
                  </div>
                  <span className="text-gray-700">Read detailed mentor profiles and student reviews</span>
                </li>
                <li className="flex items-start">
                  <div className="bg-green-100 p-1 rounded-full mr-3 mt-1">
                    <FaCheck className="text-green-600" />
                  </div>
                  <span className="text-gray-700">Book one-on-one or group sessions at your convenience</span>
                </li>
              </ul>
              <NavLink
                to="/"
                state={{ openServices: true }}
                className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                onClick={(e) => {
                  // Scroll to top immediately for better UX - menu will open at top
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Find Your Mentor <FaArrowRight className="ml-2" />
              </NavLink>
            </div>
            <div className="bg-gray-100 rounded-xl overflow-hidden shadow-md">
              <img
                src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80"
                alt="Yoga student with mentor"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* For Mentors Section */}
      <section id="for-mentors" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              For <span className="text-teal-600">Mentors</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Grow your teaching practice and connect with dedicated members
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="order-1 md:order-1 bg-gray-100 rounded-xl overflow-hidden shadow-md">
              <img
                src="https://images.unsplash.com/photo-1545205597-3d9d02c29597?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80"
                alt="Mentor teaching"
                className="w-full h-auto object-cover"
              />
            </div>
            <div className="order-2 md:order-2">
              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center mb-6">
                  <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                    <FaQuoteLeft className="text-teal-600 text-xl" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-800">Expand Your Reach</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Join our platform to connect with members who are eager to learn from experienced mentors like you.
                </p>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <div className="bg-teal-100 p-1 rounded-full mr-3 mt-1">
                      <FaCheck className="text-teal-600" />
                    </div>
                    <span className="text-gray-700">Create a professional profile to showcase your expertise</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-teal-100 p-1 rounded-full mr-3 mt-1">
                      <FaCheck className="text-teal-600" />
                    </div>
                    <span className="text-gray-700">Manage your schedule and student interactions in one place</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-teal-100 p-1 rounded-full mr-3 mt-1">
                      <FaCheck className="text-teal-600" />
                    </div>
                    <span className="text-gray-700">Get paid securely for your sessions</span>
                  </li>
                </ul>
                <NavLink
                  to="/signup"
                  className="inline-flex items-center bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                >
                  Join as Mentor <FaArrowRight className="ml-2" />
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to know about our platform
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                index={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onClick={() => toggleAccordion(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* PWA Install Section - Only show in browser mode */}
      {!isPWA && (
        <section id="pwa-install">
          <PWAInstallSection />
        </section>
      )}

      {/* Final CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-t from-dark to-primary text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Practices?</h2>
          <p className="text-xl mb-8">
            Join thousands of members who have found their perfect mentors through our platform.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <NavLink
              to="/"
              state={{ openServices: true }}
              className="bg-white text-dark hover:bg-gray-200 px-8 py-3 rounded-lg font-medium text-lg transition-colors shadow-md hover:shadow-lg"
              onClick={(e) => {
                // Scroll to top immediately for better UX - menu will open at top
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Find an Mentor
            </NavLink>
            <NavLink
              to="/signup"
              className="bg-pink-800 hover:bg-primary text-white px-8 py-3 rounded-lg font-medium text-lg transition-colors shadow-md hover:shadow-lg"
            >
              Become an Mentor
            </NavLink>
          </div>
        </div>
      </section>

    </div>
  );
}

export default Landing;